// Libs
import clsx from "clsx";
import type React from "react";

// Utils
import { alterElement, wrapKeyUp, wrapLabel, wrapPrevent } from "../../core/utils";

// Components
import { type IconPosition, type IconProps, IconWrapper, type IconWrapperProps } from "../icons";

export type DisplayType = "row" | "column" | "condensed";

export const DISPLAY_TYPES_ICON_POSITION_MAPPER: Record<DisplayType, IconPosition> = {
    row: "right",
    column: "left",
    condensed: "right",
};

export interface DisplayProps extends React.PropsWithChildren<unknown> {
    /**
     * Additional className for display
     */
    className?: string;
    /**
     * if defined add a color bubble on the left of the label
     */
    colorBubble?: string;
    /**
     * Helper icon
     */
    helperIcon?: IconProps["children"];
    /**
     * Additional className for helper Icon
     */
    helperIconClassName?: string;
    /**
     * Additional props for helper Icon
     */
    helperIconProps?: IconProps;
    /**
     * Helper text or component
     */
    iconTooltip?: string | JSX.Element;
    /**
     * Icon component
     */
    icon?: IconWrapperProps["icon"];
    /**
     * Additional props for icon
     */
    iconProps?: Partial<IconWrapperProps>;
    /**
     * Label
     */
    label?: string | JSX.Element;
    /**
     * Label icon
     */
    labelIcon?: IconProps["icon"];
    /**
     * Additional props for label icon
     */
    labelIconProps?: Partial<IconWrapperProps>;
    /**
     * Click handler
     */
    onClick?: React.MouseEventHandler<HTMLDivElement>;
    /**
     * Role to the display
     */
    role?: string;
    /**
     * Tab index value
     */
    tabIndex?: number;
    /**
     * Type of display
     */
    type?: DisplayType;
    /**
     * Value
     */
    value: React.ReactNode;
    /**
     * Additional className for value
     */
    valueClassName?: string;
    /**
     * Additional className for label
     */
    labelClassName?: string;
}

export function Display({
    children,
    className,
    colorBubble,
    icon,
    iconProps,
    iconTooltip,
    label,
    labelIcon,
    labelIconProps,
    labelClassName: labelClassNameProps,
    onClick,
    role = "button",
    tabIndex = 0,
    type = "row",
    value,
    valueClassName: valueClassNameProp,
    ...otherProps
}: DisplayProps): JSX.Element {
    const displayClassName = clsx(
        "ds-c-display",
        {
            "ds-o-actionable": !!onClick,
            "ds-c-display--horizontal": type === "row",
            "ds-c-display--vertical": type === "column",
            "ds-c-display--condensed": type === "condensed",
        },
        className
    );
    const iconClassName = clsx("ds-c-display__icon ", iconProps?.className);
    const labelIconClassName = clsx("ds-c-display__label-icon", labelIconProps?.className);
    const valueClassName = clsx("ds-c-display__value", valueClassNameProp);
    const labelClassName = clsx("ds-c-display__label", "ds-u-text--label", labelClassNameProps);

    const handleOnClick = wrapPrevent(onClick);
    const handleKeyUp = wrapKeyUp(handleOnClick);

    let child = alterElement(value ?? children, { className: valueClassName });
    let LabelComponent = <>{alterElement(wrapLabel(label), { className: labelClassName })}</>;

    if (icon) {
        child = (
            <IconWrapper
                {...iconProps}
                tooltip={iconTooltip}
                tooltipProps={{ placement: "top" }}
                className={iconClassName}
                icon={icon}
                size={iconProps?.size ?? "s"}
                position={iconProps?.position ?? DISPLAY_TYPES_ICON_POSITION_MAPPER[type]}
            >
                {child}
            </IconWrapper>
        );
    }

    if (labelIcon) {
        LabelComponent = (
            <IconWrapper
                tooltip={iconTooltip}
                tooltipProps={{ placement: "top" }}
                {...labelIconProps}
                className={labelIconClassName}
                icon={labelIcon}
                position={labelIconProps?.position ?? DISPLAY_TYPES_ICON_POSITION_MAPPER[type]}
                size={labelIconProps?.size ?? "s"}
            >
                {LabelComponent}
            </IconWrapper>
        );
    }

    return (
        /* The <div> element has a child <role> attribute that allows keyboard interaction */
        <div
            {...otherProps}
            className={displayClassName}
            onClick={handleOnClick}
            onKeyUp={handleKeyUp}
            role={onClick ? role : undefined}
            tabIndex={onClick ? tabIndex : undefined}
        >
            {label && (
                <div className="ds-c-display__label-container">
                    <div className="ds-o-flex" style={{ gap: 8 }}>
                        {colorBubble && (
                            <div
                                className="ds-c-display__color-bubble"
                                style={{ backgroundColor: colorBubble }}
                            />
                        )}
                        {LabelComponent}
                    </div>
                </div>
            )}
            {child}
        </div>
    );
}
